/// <amd-module name="Wit/BoardsHub/KanbanBoard/Components/Annotations/TestAnnotation" />

import * as React from "react";
import { useTfsProject } from "Tfs/Platform/TfsComponent";
import { useObservable } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { getMenuItems, IExternalMenuItem } from "VSS/Features/PlatformUI/ContributedMenu";
import { usePageContext } from "VSS/Platform/Layout";
import { Button } from "VSSUI/Button";
import { Icon } from "VSSUI/Icon";
import { Link } from "VSSUI/Link";
import { IListItemDetails, ListDragImage, ListItem } from "VSSUI/List";
import { IMenuItem } from "VSSUI/Menu";
import { Observer } from "VSSUI/Observer";
import { css } from "VSSUI/Util";
import { IObservableBoardsWorkItem, ITestAnnotationInfo, ITestItem, TestOutcome } from "Wit/BoardsHub/Common/Interfaces";
import { CardMenuButton } from "Wit/BoardsHub/KanbanBoard/Components/CardMenuButton";
import * as Resources from "Wit/BoardsHub/KanbanBoard/Resources";
import { getTestPlanUrl, getWorkItemUrl } from "Wit/BoardsHub/KanbanBoard/Utils/RoutingUtils";
import { getWorkItemTrackingClient } from "Wit/Clients/Wit/RestClient/WorkItemTracking";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import { IWorkItemLinksService } from "Wit/Common/WorkItemLinksService/WorkItemLinksService";
import { showErrorMessage } from "Wit/Messages/BoardsMessagesService";
import { WorkItemCategoryConstants } from "Wit/WorkItemModel/OM/WorkItemConstants";
import { hexToRgb, isDark } from "VSSUI/Utilities/Color";
import { IAnnotationService } from "../../IAnnotationService";
import { testOutcomeToByte } from "../../Utils/TestAnnotationUtils";
import { getDefaultMenuItems, onWorkItemClick } from "../Util";
import { AnnotationComponent, AnnotationItemTextbox } from "./Common";
import { DraggableAnnotationList } from "./DraggableAnnotationList";

/**
 * Implements the annotation for tests
 */
export const TestAnnotation: AnnotationComponent = ({ annotations, expanded, name, onClick, item, onModifyItems }) => {
    const info = annotations?.tests;
    const openInAddingMode = item.addingAnnotation && expanded;
    if (!openInAddingMode && (!info || !info.tests.length)) {
        return null;
    }

    const itemsColor: string | undefined = item.value.backgroundColor
        ? isDark(hexToRgb(item.value.backgroundColor))
            ? css("dark")
            : css("light")
        : undefined;

    const count = info?.tests.length ?? 0;
    const passed = info?.passed ?? 0;
    const failed = info?.failed ?? 0;
    const notApplicable = info?.notApplicable ?? 0;
    const other = (info?.other ?? 0) + notApplicable;
    const label = format(Resources.TestAnnotationLabel, passed, failed, other);
    return (
        <>
            <Button
                ariaLabel={label}
                ariaExpanded={expanded}
                className={css("annotation", expanded && "active", itemsColor)}
                iconProps={{ render: className => <TestStatusIcon annotationInfo={info} className={className} /> }}
                onClick={() => onClick(name)}
                subtle
                tooltipProps={{ text: label }}
            >
                {count}
            </Button>
            {expanded && <TestAnnotationDetails parent={item} testInfo={info} itemsColor={itemsColor} onModifyItems={onModifyItems} />}
        </>
    );
};

function TestAnnotationDetails({
    parent,
    testInfo,
    itemsColor,
    onModifyItems
}: {
    parent: IObservableBoardsWorkItem;
    testInfo?: ITestAnnotationInfo;
    itemsColor: string | undefined;
    onModifyItems?: () => void;
}) {
    const [adding, setAdding] = React.useState(parent.addingAnnotation);
    const [promises, setPromises] = React.useState<{ typePromise: Promise<string>; linkTypePromise: Promise<number> }>();
    const pageContext = usePageContext();
    const projectId = useTfsProject()?.id ?? "";
    const [editingTitle] = useObservable<number | undefined>(0);
    const annotationService = pageContext.getService<IAnnotationService>("IAnnotationService");

    const renderTestRow = (rowIndex: number, item: ITestItem, details: IListItemDetails<ITestItem | string>) => {
        const menuItems = getDefaultMenuItems(pageContext, item.id, () => (editingTitle.value = item.id));

        const processExternalMenuItem = (menuItem: IMenuItem, contribution: IMenuItem | IExternalMenuItem) => {
            if ((menuItem as IExternalMenuItem).hidden) {
                return undefined;
            }

            return menuItem;
        };

        menuItems.push(getTestMenuItems(projectId, parent, item, annotationService));
        menuItems.push(
            getMenuItems(
                pageContext,
                "ms.vss-test-web.wit-test-point-context",
                {
                    onErrorDelegate: (error: any) => showErrorMessage(pageContext, error),
                    id: item.id,
                    requirementId: parent.value.id,
                    testPlanId: testInfo?.planId,
                    testPointId: item.testPointId,
                    testPointOutCome: testOutcomeToByte(item.outcome),
                    testSuiteId: testInfo?.suiteId
                },
                { processExternalMenuItem }
            )
        );

        return (
            <ListItem className={css("annotation-row rhythm-horizontal-4", itemsColor)} key={item.id} index={rowIndex} details={details}>
                {getTestOutcomeIcon(item.outcome)}
                <Observer editingTitle={editingTitle}>
                    {() =>
                        editingTitle.value === item.id ? (
                            <AnnotationItemTextbox
                                onEdit={title => (item.title = title)}
                                onComplete={() => (editingTitle.value = undefined)}
                                parent={parent}
                                workItemId={item.id}
                                title={item.title}
                            />
                        ) : (
                            <Link
                                className="title title-text"
                                href={getWorkItemUrl(pageContext, item.id)}
                                onClick={e => onWorkItemClick(pageContext, e, item.id, item, parent)}
                            >
                                {item.title}
                            </Link>
                        )
                    }
                </Observer>
                <CardMenuButton className="annotation-context-menu" menuItems={menuItems} />
            </ListItem>
        );
    };

    const onDrop = (item: ITestItem, parentId: number, previousParentId?: number, newIndex?: number) => {
        if (!projectId) {
            return;
        }

        const parentChanged = parentId !== previousParentId;
        const annotationService = pageContext.getService<IAnnotationService>("IAnnotationService");
        annotationService.moveTestAnnotation(projectId, parentId, item, newIndex, parentChanged ? previousParentId : undefined);
    };

    React.useEffect(() => {
        if (!promises && adding) {
            // get test case work item type
            const witClient = getWorkItemTrackingClient(pageContext);
            const typePromise = witClient
                .getWorkItemTypeCategory(projectId, WorkItemCategoryConstants.TEST_CASE)
                .then(v => v.defaultWorkItemType.name);

            // get link type
            const linksService = pageContext.getService<IWorkItemLinksService>("IWorkItemLinksService");
            const linkTypePromise = linksService
                .getWitLinkTypes()
                .then(linkTypes => linkTypes.find(lt => lt.referenceName === "Microsoft.VSTS.Common.TestedBy")!.reverseEnd.id);

            setPromises({ typePromise, linkTypePromise });
        }
    }, [adding]);

    React.useEffect(() => {
        if (parent.addingAnnotation && !adding) {
            setAdding(true);
        }
    }, [parent.addingAnnotation]);

    return (
        <div className="annotation-expanded test-annotation-details">
            <Button
                // this button is needed to be found by the test runner and clicked to update the test state
                className={`kanban-board-invisible-reload-button-${parent.value.id} hidden`}
                onClick={onModifyItems}
            ></Button>
            <div className="flex-row margin-top-8">
                <Button
                    className={css("annotation-button", itemsColor)}
                    iconProps={{ iconName: "Add" }}
                    onClick={() => {
                        parent.addingAnnotation = true;
                        setAdding(true);
                    }}
                >
                    {Resources.AddTest}
                </Button>
                {testInfo?.planId && testInfo?.suiteId && (
                    <>
                        <div className={css("test-annotation-divider margin-left-8 margin-right-4", itemsColor)}/>
                        <Link href={getTestPlanUrl(pageContext, projectId, testInfo.planId, testInfo.suiteId)} target="_blank">
                            <Button className={css("annotation-button", "not-highlighted", itemsColor)} iconProps={{ iconName: "ReplyMirrored" }} subtle />
                        </Link>
                    </>
                )}
            </div>
            {testInfo && (
                <DraggableAnnotationList<ITestItem>
                    ariaLabel={Resources.TestAnnotationTableAriaLabel}
                    dragType="test-annotation"
                    items={testInfo.tests}
                    onDropItem={onDrop}
                    parent={parent}
                    renderDragImage={event => {
                        const testItem = event.detail.dataTransfer.data;
                        return (
                            <ListDragImage
                                iconProps={{
                                    render: () => getTestOutcomeIcon(testItem.outcome)
                                }}
                                text={testItem.title}
                            />
                        );
                    }}
                    renderRow={renderTestRow}
                />
            )}
            {adding && promises && (
                <div className="new-item flex-row flex-baseline">
                    {getTestOutcomeIcon(TestOutcome.Active)}
                    <AnnotationItemTextbox
                        linkType={promises.linkTypePromise}
                        onAdd={newItem => {
                            annotationService.addTestAnnotation(parent, {
                                id: newItem.id,
                                outcome: TestOutcome.Active,
                                title: newItem.getFieldValueByName(CoreFieldRefNames.Title)
                            });
                        }}
                        onComplete={(createExtraItem: boolean) => {
                            parent.addingAnnotation = false;
                            setAdding(false);
                            if (createExtraItem) {
                                parent.addingAnnotation = true;
                                setAdding(true);
                            }
                        }}
                        parent={parent}
                        type={promises.typePromise}
                    />
                </div>
            )}
        </div>
    );
}

function TestStatusIcon({ annotationInfo, className }: { annotationInfo?: ITestAnnotationInfo; className?: string }) {
    return (
        <>
            <Icon className={css("bowtie-icon bowtie-test-fill", className)} />
            {annotationInfo && <TestStatusOverlay annotationInfo={annotationInfo} />}
        </>
    );
}

function TestStatusOverlay({ annotationInfo }: { annotationInfo: ITestAnnotationInfo }) {
    const showSuccess = annotationInfo.tests.length > 0 && annotationInfo.tests.length === annotationInfo.passed;
    const showFailure = annotationInfo.failed > 0;
    if (!showSuccess && !showFailure) {
        return null;
    }

    return (
        <>
            {/** put a white background behind the status icon */}
            {<Icon className="test-status-overlay-background body-xs margin-right-4" iconName="CircleShapeSolid" />}
            {getTestOutcomeIcon(showSuccess ? TestOutcome.Passed : TestOutcome.Failed, "test-status-overlay")}
        </>
    );
}

function getTestOutcomeIcon(outcome: TestOutcome, className?: string): JSX.Element {
    className = css("body-xs margin-right-4", className);
    switch (outcome) {
        case TestOutcome.Active:
            return <Icon ariaLabel={Resources.Active} className={className} iconName="CircleShapeSolid" style={{ color: "rgb(var(--palette-primary))" }} />;
        case TestOutcome.Passed:
            return <Icon ariaLabel={Resources.Passed} className={className} iconName="CompletedSolid" style={{ color: "rgb(var(--palette-accent2-dark))" }} />;
        case TestOutcome.Failed:
            return <Icon ariaLabel={Resources.Failed} className={className} iconName="StatusErrorFull" style={{ color: "rgb(var(--palette-accent1))" }} />;
        case TestOutcome.Blocked:
            return <Icon ariaLabel={Resources.Blocked} className={className} iconName="Blocked2Solid" style={{ color: "rgb(var(--palette-accent1-dark))" }} />;
        case TestOutcome.NotApplicable:
            return <Icon ariaLabel={Resources.NotApplicable} className={className} iconName="Prohibited" style={{ color: "rgb(var(--palette-accent1))" }} />;
        case TestOutcome.Paused:
            return <Icon ariaLabel={Resources.Paused} className={className} iconName="CirclePauseSolid" style={{ color: "rgb(var(--palette-primary-tint-10))" }} />;
        case TestOutcome.InProgress:
            return <Icon ariaLabel={Resources.InProgress} className={className} iconName="MSNVideosSolid" style={{ color: "rgb(var(--palette-accent2-dark))" }} />;
    }
}

function getTestMenuItems(projectId: string, parent: IObservableBoardsWorkItem, item: ITestItem, annotationService: IAnnotationService): IMenuItem[] {
    const onSetTestOutcome = (outcome: TestOutcome) => {
        annotationService.setTestOutcome(projectId, parent, item, outcome);
    };

    return [
        {
            id: "remove-test-case",
            groupKey: "test-case-options",
            text: Resources.RemoveTest,
            rank: 90,
            iconProps: { ariaHidden: true, iconName: "ChromeClose" },
            onActivate: () => {
                annotationService.removeTestAnnotation(projectId, parent, item);
            }
        },
        getTestMenuItemForOutcome(TestOutcome.Active, Resources.ResetToActive, 100, onSetTestOutcome, "Undo"),
        getTestMenuItemForOutcome(TestOutcome.Passed, Resources.PassTest, 110, onSetTestOutcome),
        getTestMenuItemForOutcome(TestOutcome.Failed, Resources.FailTest, 120, onSetTestOutcome),
        getTestMenuItemForOutcome(TestOutcome.Blocked, Resources.BlockTest, 130, onSetTestOutcome),
        getTestMenuItemForOutcome(TestOutcome.NotApplicable, Resources.NotApplicable, 140, onSetTestOutcome)
    ];
}

function getTestMenuItemForOutcome(
    outcome: TestOutcome,
    label: string,
    rank: number,
    onSetTestOutcome: (outcome: TestOutcome) => void,
    iconName?: string
): IMenuItem {
    return {
        id: outcome.toString(),
        groupKey: "test-case-options",
        text: label,
        rank,
        iconProps: iconName ? { ariaHidden: true, iconName } : { render: className => getTestOutcomeIcon(outcome, className) },
        onActivate: () => {
            onSetTestOutcome(outcome);
        }
    };
}
